import React, { useState, useEffect, useRef } from 'react';
import { MessageCircle, X, Send, Loader } from 'lucide-react';

const ChatBot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState('');
  const [adminTookOver, setAdminTookOver] = useState(false);
  const [conversationClosed, setConversationClosed] = useState(false);
  const [canReopen, setCanReopen] = useState(false);
  const [reopenSecondsLeft, setReopenSecondsLeft] = useState(0);
  const REOPEN_WINDOW_MS = 5 * 60 * 1000; // 5 minutes
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const pollingIntervalRef = useRef(null);
  const isAtBottomRef = useRef(true);        // Suit si l'utilisateur est en bas
  const lastServerMsgCountRef = useRef(0);   // Compte des messages serveur connus
  const sessionLoadedRef = useRef(false);    // Historique chargé depuis le serveur ?
  
  const API_URL = window.location.origin;

  // AVATARS DISPONIBLES - Change facilement l'avatar ici !
  const AVATARS = {
    sophie_femme: {
      name: "Sophie",
      image: "https://images.pexels.com/photos/7709098/pexels-photo-7709098.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
      initial: "S",
      gender: "femme"
    },
    marie_femme: {
      name: "Marie",
      image: "https://images.pexels.com/photos/7682357/pexels-photo-7682357.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
      initial: "M",
      gender: "femme"
    },
    alex_homme: {
      name: "Alex",
      image: "https://images.unsplash.com/photo-1577880216142-8549e9488dad",
      initial: "A",
      gender: "homme"
    },
    karim_homme: {
      name: "Karim",
      image: "https://images.pexels.com/photos/7709115/pexels-photo-7709115.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
      initial: "K",
      gender: "homme"
    }
  };

  // CHOISIS L'AVATAR ICI (change "sophie_femme" par l'avatar de ton choix)
  const CURRENT_AVATAR = AVATARS.sophie_femme;

  // ── Initialisation : session persistante dans localStorage ─────────────────
  useEffect(() => {
    let saved = localStorage.getItem('phonelab_session_id');
    if (!saved) {
      saved = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      localStorage.setItem('phonelab_session_id', saved);
    }
    setSessionId(saved);
  }, []);

  // ── Scroll intelligent : uniquement si déjà en bas ─────────────────────────
  useEffect(() => {
    if (isAtBottomRef.current) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  const handleScroll = () => {
    const container = messagesContainerRef.current;
    if (!container) return;
    const { scrollTop, scrollHeight, clientHeight } = container;
    isAtBottomRef.current = scrollHeight - scrollTop - clientHeight < 80;
  };

  // ── Ouverture du chat ───────────────────────────────────────────────────────
  useEffect(() => {
    if (isOpen && sessionId && !sessionLoadedRef.current) {
      sessionLoadedRef.current = true;
      loadOrInitConversation();
    }
    if (!isOpen && pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
    return () => {
      if (pollingIntervalRef.current) clearInterval(pollingIntervalRef.current);
    };
  }, [isOpen, sessionId]);

  // ── Charger historique ou afficher message de bienvenue ────────────────────
  const loadOrInitConversation = async () => {
    try {
      const resp = await fetch(`${API_URL}/api/chat/conversation/${sessionId}`);
      if (resp.ok) {
        const data = await resp.json();
        const serverMsgs = data.messages || [];
        if (serverMsgs.length > 0) {
          // Conversation existante : afficher l'historique
          const displayMsgs = serverMsgs.filter(m => m.role !== 'system');
          setMessages(displayMsgs);
          lastServerMsgCountRef.current = serverMsgs.length;
          // Vérifier si un admin avait pris la main
          const hasAdmin = serverMsgs.some(m => m.role === 'admin');
          if (hasAdmin) {
            setAdminTookOver(true);
            // Ajouter notification si pas encore présente
            setMessages(prev => {
              const alreadyShown = prev.some(m => m.role === 'system' && m.content === 'admin_takeover');
              if (!alreadyShown) return [...prev, { role: 'system', content: 'admin_takeover', timestamp: new Date().toISOString() }];
              return prev;
            });
          }
          if (data.closed) {
            const endedAt = parseInt(localStorage.getItem('phonelab_session_ended_at') || '0');
            const elapsed = Date.now() - endedAt;
            if (endedAt && elapsed < REOPEN_WINDOW_MS) {
              setConversationClosed(true);
              setCanReopen(true);
              setReopenSecondsLeft(Math.ceil((REOPEN_WINDOW_MS - elapsed) / 1000));
            } else {
              // > 5 min ou pas de timestamp → nouvelle conversation auto
              startNewConversation();
              return;
            }
          }
        } else {
          // Nouvelle conversation
          showWelcomeMessage();
        }
      } else {
        showWelcomeMessage();
      }
    } catch {
      showWelcomeMessage();
    }
    startPolling();
    // Forcer le scroll en bas après chargement
    setTimeout(() => {
      isAtBottomRef.current = true;
      messagesEndRef.current?.scrollIntoView({ behavior: 'instant' });
    }, 100);
  };

  const showWelcomeMessage = () => {
    setMessages([{
      role: 'assistant',
      content: `Salut ! Moi c'est ${CURRENT_AVATAR.name}, je bosse chez PhoneLab 69 😊🔧\n\nComment je peux t'aider aujourd'hui ?`,
      timestamp: new Date().toISOString()
    }]);
    lastServerMsgCountRef.current = 0;
  };

  // ── Polling : uniquement nouveaux messages admin ──────────────────────────
  const startPolling = () => {
    if (pollingIntervalRef.current) return;
    pollingIntervalRef.current = setInterval(checkForNewMessages, 1000);
  };

  const checkForNewMessages = async () => {
    if (!sessionId || conversationClosed) return;
    try {
      const response = await fetch(`${API_URL}/api/chat/conversation/${sessionId}`);
      if (!response.ok) return;
      const data = await response.json();
      const serverMsgs = data.messages || [];
      const serverCount = serverMsgs.length;

      // Seulement si de nouveaux messages sont arrivés
      if (serverCount > lastServerMsgCountRef.current) {
        const newMsgs = serverMsgs.slice(lastServerMsgCountRef.current);
        lastServerMsgCountRef.current = serverCount;

        // Ajouter uniquement les messages admin (les messages user/assistant sont déjà affichés localement)
        const adminMsgs = newMsgs.filter(m => m.role === 'admin');
        if (adminMsgs.length > 0) {
          setMessages(prev => {
            const alreadyHasTakeover = prev.some(m => m.role === 'system' && m.content === 'admin_takeover');
            const newItems = [];
            if (!alreadyHasTakeover) {
              newItems.push({ role: 'system', content: 'admin_takeover', timestamp: new Date().toISOString() });
              setAdminTookOver(true);
            }
            newItems.push(...adminMsgs);
            // Forcer scroll en bas pour messages admin importants
            isAtBottomRef.current = true;
            return [...prev, ...newItems];
          });
        }
      }

      if (data.closed) {
        const endedAt = parseInt(localStorage.getItem('phonelab_session_ended_at') || '0');
        const elapsed = Date.now() - endedAt;
        if (!canReopen) {
          if (endedAt && elapsed < REOPEN_WINDOW_MS) {
            setConversationClosed(true);
            setCanReopen(true);
            setReopenSecondsLeft(Math.ceil((REOPEN_WINDOW_MS - elapsed) / 1000));
          } else {
            setConversationClosed(true);
          }
        }
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    } catch { /* silencieux */ }
  };

  // Fonction pour terminer la conversation
  const endConversation = async () => {
    try {
      const response = await fetch(`${API_URL}/api/chat/end`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          session_id: sessionId
        })
      });
      
      if (response.ok) {
        localStorage.setItem('phonelab_session_ended_at', Date.now().toString());
        setConversationClosed(true);
        setCanReopen(true);
        setReopenSecondsLeft(300); // 5 min
        setMessages(prev => [...prev, {
          role: 'system',
          content: 'conversation_ended',
          timestamp: new Date().toISOString()
        }]);
        
        // Arrêter le polling
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current);
          pollingIntervalRef.current = null;
        }
      }
    } catch (error) {
      console.error('Erreur fermeture conversation:', error);
    }
  };

  // Reprendre une conversation fermée (dans les 5 min)
  const handleReopen = async () => {
    try {
      await fetch(`${API_URL}/api/chat/conversations/${sessionId}/reopen`, { method: 'POST' });
    } catch { /* silencieux */ }
    localStorage.removeItem('phonelab_session_ended_at');
    setConversationClosed(false);
    setCanReopen(false);
    setReopenSecondsLeft(0);
    // Retirer le message système "conversation_ended"
    setMessages(prev => prev.filter(m => !(m.role === 'system' && m.content === 'conversation_ended')));
    startPolling();
  };

  // Démarrer une nouvelle conversation
  const startNewConversation = () => {
    const newSession = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    localStorage.setItem('phonelab_session_id', newSession);
    localStorage.removeItem('phonelab_session_ended_at');
    setSessionId(newSession);
    setMessages([]);
    setConversationClosed(false);
    setCanReopen(false);
    setReopenSecondsLeft(0);
    setAdminTookOver(false);
    sessionLoadedRef.current = false;
    lastServerMsgCountRef.current = 0;
    showWelcomeMessage();
    startPolling();
  };

  // Compte à rebours réouverture — expire → nouvelle conversation automatique
  useEffect(() => {
    if (!canReopen || reopenSecondsLeft <= 0) return;
    const timer = setInterval(() => {
      setReopenSecondsLeft(prev => {
        if (prev <= 1) {
          clearInterval(timer);
          setCanReopen(false);
          startNewConversation();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [canReopen]);

  const sendMessage = async () => {
    if (!inputMessage.trim() || isLoading) return;

    const userMessage = inputMessage.trim();
    setInputMessage('');
    
    const newUserMessage = {
      role: 'user',
      content: userMessage,
      timestamp: new Date().toISOString()
    };
    
    setMessages(prev => [...prev, newUserMessage]);
    isAtBottomRef.current = true;
    setIsLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: userMessage,
          session_id: sessionId
          // Pas de conversation_history : le backend charge depuis la DB
        })
      });

      const data = await response.json();
      
      // Si admin a pris le relais, ne pas afficher de réponse bot
      if (data.admin_takeover) {
        setIsLoading(false);
        return;
      }
      
      if (data.response) {
        const botMessage = {
          role: 'assistant',
          content: data.response,
          timestamp: new Date().toISOString()
        };
        setMessages(prev => [...prev, botMessage]);
      }
      
      if (data.should_create_request) {
        setTimeout(() => {
          setMessages(prev => [...prev, {
            role: 'system',
            content: 'create_request_options',
            timestamp: new Date().toISOString()
          }]);
        }, 500);
      }
      
    } catch (error) {
      console.error('Erreur chatbot:', error);
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: "Désolé, je rencontre un problème technique. Pouvez-vous réessayer ou nous appeler au 04 26 00 82 10 ?",
        timestamp: new Date().toISOString()
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateRequest = async (requestType) => {
    setIsLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/chat/create-request`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          session_id: sessionId,
          request_type: requestType
        })
      });

      const data = await response.json();
      
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: `✅ ${data.message}\n\nVous recevrez une confirmation par email. Notre équipe vous contactera très prochainement !`,
        timestamp: new Date().toISOString()
      }]);
      
    } catch (error) {
      console.error('Erreur création demande:', error);
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: "Désolé, impossible de créer votre demande. Contactez-nous au 04 26 00 82 10",
        timestamp: new Date().toISOString()
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <>
      {/* Avatar virtuel flottant */}
      {!isOpen && (
        <button
          onClick={() => setIsOpen(true)}
          className="fixed bottom-6 left-6 z-[10000] group"
        >
          {/* Avatar avec effet de pulsation */}
          <div className="relative">
            {/* Cercles de pulsation */}
            <span className="absolute inset-0 rounded-full bg-blue-400 animate-ping opacity-75"></span>
            <span className="absolute inset-0 rounded-full bg-blue-300 animate-pulse"></span>
            
            {/* Avatar image */}
            <div className="relative w-20 h-20 rounded-full overflow-hidden border-4 border-white shadow-2xl transform transition-all duration-300 group-hover:scale-110">
              <img 
                src={CURRENT_AVATAR.image}
                alt={`${CURRENT_AVATAR.name} - Conseiller PhoneLab`}
                className="w-full h-full object-cover"
              />
              {/* Indicateur en ligne */}
              <div className="absolute bottom-1 right-1 w-4 h-4 bg-green-500 rounded-full border-2 border-white animate-pulse"></div>
            </div>
          </div>
          
          {/* Badge "Besoin d'aide ?" TOUJOURS visible */}
          <div className="absolute left-24 top-1/2 -translate-y-1/2 pointer-events-none">
            <div className="bg-gradient-to-r from-blue-500 to-purple-500 text-white px-4 py-2 rounded-lg shadow-xl whitespace-nowrap animate-bounce-slow">
              <p className="font-bold text-sm">💬 Besoin d'aide ?</p>
              <p className="text-xs opacity-90">Clique pour discuter avec {CURRENT_AVATAR.name}</p>
            </div>
          </div>
        </button>
      )}

      {/* Fenêtre de chat */}
      {isOpen && (
        <div className="fixed bottom-6 left-6 w-96 h-[600px] bg-white rounded-2xl shadow-2xl flex flex-col z-[10000] border-2 border-blue-500">
          {/* Header */}
          <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white p-4 rounded-t-2xl flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-white rounded-full flex items-center justify-center">
                <MessageCircle className="text-blue-600" size={20} />
              </div>
              <div>
                <h3 className="font-bold text-lg">PhoneLab Assistant</h3>
                <p className="text-xs text-blue-100">En ligne • Répond en ~2 sec</p>
              </div>
            </div>
            <button
              onClick={() => setIsOpen(false)}
              className="hover:bg-blue-800 rounded-full p-2 transition"
            >
              <X size={20} />
            </button>
          </div>

          {/* Messages */}
          <div
            ref={messagesContainerRef}
            onScroll={handleScroll}
            className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50"
          >
            {messages.map((msg, index) => (
              <div key={index}>
                {msg.role === 'user' ? (
                  <div className="flex justify-end">
                    <div className="bg-blue-600 text-white rounded-2xl rounded-tr-none px-4 py-2 max-w-[80%] shadow">
                      <p className="whitespace-pre-wrap">{msg.content}</p>
                    </div>
                  </div>
                ) : msg.role === 'admin' ? (
                  /* Message de l'admin */
                  <div className="flex justify-start">
                    <div className="space-y-2 max-w-[80%]">
                      <div className="bg-gradient-to-r from-purple-100 to-pink-100 border-2 border-purple-300 rounded-2xl rounded-tl-none px-4 py-3 shadow-lg">
                        <div className="flex items-center space-x-2 mb-1">
                          <span className="text-lg">🧑‍💼</span>
                          <span className="text-xs font-bold text-purple-700">Agent PhoneLab</span>
                        </div>
                        <p className="whitespace-pre-wrap text-gray-900">{msg.content}</p>
                      </div>
                    </div>
                  </div>
                ) : msg.role === 'system' && msg.content === 'admin_takeover' ? (
                  /* Notification qu'un admin a pris le relais */
                  <div className="flex justify-center my-4">
                    <div className="bg-gradient-to-r from-green-500 to-teal-500 text-white px-6 py-3 rounded-full shadow-lg animate-pulse">
                      <p className="text-sm font-bold flex items-center space-x-2">
                        <span>✅</span>
                        <span>Un agent a pris le relais de la conversation</span>
                      </p>
                    </div>
                  </div>
                ) : msg.role === 'system' && msg.content === 'conversation_ended' ? (
                  /* Notification de fin de conversation */
                  <div className="flex justify-center my-4">
                    <div className="bg-gray-500 text-white px-6 py-3 rounded-full shadow-lg">
                      <p className="text-sm font-bold">Conversation terminée</p>
                    </div>
                  </div>
                ) : msg.role === 'system' && msg.content === 'create_request_options' ? (
                  <div className="flex flex-col space-y-2">
                    <p className="text-sm text-gray-600 text-center mb-2">
                      Que souhaitez-vous faire ?
                    </p>
                    <button
                      onClick={() => handleCreateRequest('devis')}
                      disabled={isLoading}
                      className="bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-lg font-semibold transition disabled:opacity-50"
                    >
                      📝 Recevoir un devis gratuit
                    </button>
                    <button
                      onClick={() => handleCreateRequest('rdv')}
                      disabled={isLoading}
                      className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg font-semibold transition disabled:opacity-50"
                    >
                      📅 Prendre rendez-vous
                    </button>
                    <button
                      onClick={() => handleCreateRequest('diagnostic')}
                      disabled={isLoading}
                      className="bg-orange-500 hover:bg-orange-600 text-white px-4 py-2 rounded-lg font-semibold transition disabled:opacity-50"
                    >
                      🔍 Diagnostic gratuit
                    </button>
                  </div>
                ) : (
                  <div className="flex justify-start">
                    <div className="space-y-2 max-w-[80%]">
                      <div className="bg-white border border-gray-200 rounded-2xl rounded-tl-none px-4 py-2 shadow">
                        <p className="whitespace-pre-wrap text-gray-800">{msg.content}</p>
                      </div>
                      
                      {/* Photos automatiques si mention de localisation */}
                      {(msg.content.toLowerCase().includes('paul verlaine') || 
                        msg.content.toLowerCase().includes('située') || 
                        msg.content.toLowerCase().includes('situés') ||
                        msg.content.toLowerCase().includes('adresse')) && (
                        <div className="bg-white rounded-lg p-3 shadow-md border border-blue-200">
                          <p className="text-xs text-blue-600 mb-2 font-semibold">📸 Notre façade K&O Phonelab :</p>
                          <div className="grid grid-cols-2 gap-2 mb-2">
                            <img 
                              src="https://customer-assets.emergentagent.com/job_tech-fix-shop/artifacts/1h5fab2z_2026-01-23%20%281%29.jpg" 
                              alt="K&O Phonelab façade"
                              className="w-full h-20 object-cover rounded cursor-pointer hover:opacity-80 transition border border-gray-200"
                              onClick={() => window.open('https://customer-assets.emergentagent.com/job_tech-fix-shop/artifacts/1h5fab2z_2026-01-23%20%281%29.jpg', '_blank')}
                            />
                            <img 
                              src="https://customer-assets.emergentagent.com/job_tech-fix-shop/artifacts/tpyyr30y_unnamed.jpg" 
                              alt="Tram T6 Gratte-Ciel"
                              className="w-full h-20 object-cover rounded cursor-pointer hover:opacity-80 transition border border-gray-200"
                              onClick={() => window.open('https://customer-assets.emergentagent.com/job_tech-fix-shop/artifacts/tpyyr30y_unnamed.jpg', '_blank')}
                            />
                          </div>
                          <p className="text-xs text-gray-600 mb-2">
                            ✅ Enseigne K&O PHONELAB bleue | Tram T6 Gratte-Ciel
                          </p>
                          <a 
                            href="https://maps.app.goo.gl/b8ZpbjBDvVdmwvEv8" 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="block w-full bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white text-center py-2 px-3 rounded text-xs font-semibold transition"
                          >
                            🗺️ Voir sur Google Maps
                          </a>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ))}
            
            {isLoading && (
              <div className="flex justify-start">
                <div className="bg-white border border-gray-200 rounded-2xl rounded-tl-none px-4 py-2 shadow">
                  <div className="flex items-center space-x-2">
                    <Loader className="animate-spin text-blue-600" size={16} />
                    <span className="text-gray-500 text-sm">En train d'écrire...</span>
                  </div>
                </div>
              </div>
            )}
            
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className="p-4 border-t border-gray-200 bg-white rounded-b-2xl">
            {!conversationClosed ? (
              <>
                <div className="flex items-center space-x-2">
                  <input
                    type="text"
                    value={inputMessage}
                    onChange={(e) => setInputMessage(e.target.value)}
                    onKeyPress={handleKeyPress}
                    placeholder={adminTookOver ? "Un agent vous répond..." : "Tapez votre message..."}
                    disabled={isLoading}
                    className="flex-1 border border-gray-300 rounded-full px-4 py-2 focus:outline-none focus:border-blue-500 disabled:bg-gray-100"
                  />
                  <button
                    onClick={sendMessage}
                    disabled={!inputMessage.trim() || isLoading}
                    className="bg-blue-600 hover:bg-blue-700 text-white rounded-full p-2 disabled:opacity-50 disabled:cursor-not-allowed transition"
                  >
                    <Send size={20} />
                  </button>
                </div>
                
                {/* Bouton terminer la conversation */}
                {!adminTookOver && messages.length > 2 && (
                  <button
                    onClick={endConversation}
                    className="w-full mt-2 text-xs text-gray-500 hover:text-red-600 underline transition"
                  >
                    Terminer la conversation
                  </button>
                )}
                
                <p className="text-xs text-gray-400 mt-2 text-center">
                  {CURRENT_AVATAR.name} • Conseiller{CURRENT_AVATAR.gender === 'femme' ? 'ère' : ''} PhoneLab 69 😊
                </p>
              </>
            ) : (
              <div className="text-center py-4">
                {canReopen ? (
                  <>
                    <p className="text-gray-700 text-sm font-semibold mb-1">Conversation terminée</p>
                    <p className="text-xs text-blue-600 font-medium mb-3">
                      ⏱ Reprendre sous {Math.floor(reopenSecondsLeft / 60)}:{String(reopenSecondsLeft % 60).padStart(2, '0')}
                    </p>
                    <button
                      onClick={handleReopen}
                      className="w-full mb-2 bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded-xl text-sm font-semibold transition"
                    >
                      🔄 Reprendre la conversation
                    </button>
                    <button
                      onClick={startNewConversation}
                      className="w-full text-xs text-gray-500 hover:text-gray-700 underline transition"
                    >
                      Commencer une nouvelle discussion
                    </button>
                  </>
                ) : (
                  <>
                    <p className="text-gray-600 text-sm mb-2">Conversation terminée</p>
                    <p className="text-xs text-gray-400 mb-3">Merci d'avoir contacté PhoneLab 69 ! 🔧</p>
                    <button
                      onClick={startNewConversation}
                      className="w-full bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded-xl text-sm font-semibold transition"
                    >
                      💬 Nouvelle conversation
                    </button>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default ChatBot;
